import {
  addMonths,
  type CompareReport,
  type ComparedAmount,
  compareMoney,
  type DebtsReport,
  type DebtsReportSide,
  type IncomeExpenseReport,
  isNegativeMoney,
  isZeroMoney,
  monthBounds,
  negateMoney,
  percentOf,
  type SpendingReport,
} from '@familywallet/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Minus,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Card, CardTitle, ListGroup, SectionLabel } from '../components/ui/card';
import { Segmented } from '../components/ui/segmented';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { reportsKey } from '../lib/ledger';
import { useMembers } from '../lib/members';
import { formatAmount } from '../lib/money';
import { cn } from '../lib/utils';

function useFormat() {
  const { i18n } = useTranslation();
  return {
    money: (amount: string, currency: string, signed = false) =>
      formatAmount(amount, currency, i18n.language, { signed }),
    month: (month: string, style: 'long' | 'short' = 'long') =>
      new Intl.DateTimeFormat(i18n.language, {
        month: style,
        ...(style === 'long' ? { year: 'numeric' } : {}),
        timeZone: 'UTC',
      }).format(new Date(`${month}-01T00:00:00Z`)),
    days: (from: string, to: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }).formatRange(new Date(`${from}T12:00:00Z`), new Date(`${to}T12:00:00Z`)),
  };
}

/** A labelled horizontal bar: the width carries the value, the text always says it too. */
function Bar({ percent, tone }: { percent: string | null; tone: 'expense' | 'accent' }) {
  const width = percent === null ? 0 : Math.min(100, Number.parseInt(percent, 10));
  return (
    <div className="h-2 overflow-hidden rounded-full bg-line" aria-hidden="true">
      <div
        className={cn('h-full rounded-full', tone === 'expense' ? 'bg-expense' : 'bg-accent')}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function activityLink(categoryId: string, from: string, to: string): string {
  return `/activity?${new URLSearchParams({ categoryId, from, to }).toString()}`;
}

const rowLink =
  'flex min-h-14 flex-col gap-2 px-4 py-3 hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent';

/** 1. Where the money went: categories (subcategories rolled up), share of the total, bars. */
function SpendingSection({ report }: { report: SpendingReport }) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <section className="flex flex-col gap-2" aria-labelledby="where-title">
      <SectionLabel id="where-title">{t('reports.where.title')}</SectionLabel>
      <Card className="flex flex-col gap-1">
        <span className="text-[13px] text-muted">{t('reports.where.total')}</span>
        <span dir="ltr" className="text-2xl font-semibold tabular-nums text-start">
          {f.money(report.total, report.currency)}
        </span>
      </Card>
      {report.categories.length === 0 ? (
        <Card>
          <p className="text-[15px] text-muted">{t('reports.where.empty')}</p>
        </Card>
      ) : (
        <ListGroup>
          {report.categories.map((c) => (
            <li key={c.categoryId}>
              <Link to={activityLink(c.categoryId, report.from, report.to)} className={rowLink}>
                <span className="flex items-baseline gap-3">
                  <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{c.name}</span>
                  <span dir="ltr" className="text-[15px] font-semibold tabular-nums">
                    {f.money(c.amount, report.currency)}
                  </span>
                  <span className="w-11 text-end text-[13px] text-muted tabular-nums">
                    {c.percent === null ? '' : t('reports.percent', { percent: c.percent })}
                  </span>
                </span>
                <Bar percent={c.percent} tone="expense" />
              </Link>
              {c.subcategories.length > 0 && (
                <ul className="flex flex-col pb-2">
                  {c.subcategories.map((s) => (
                    <li key={s.categoryId}>
                      <Link
                        to={activityLink(s.categoryId, report.from, report.to)}
                        className="flex min-h-11 items-center gap-3 ps-8 pe-4 text-[13px] hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
                      >
                        <span className="min-w-0 flex-1 truncate text-muted">{s.name}</span>
                        <span dir="ltr" className="tabular-nums">
                          {f.money(s.amount, report.currency)}
                        </span>
                        <span className="w-11 text-end text-muted tabular-nums">
                          {s.percent === null ? '' : t('reports.percent', { percent: s.percent })}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ListGroup>
      )}
    </section>
  );
}

/** "+$20.00 (+50%)" with an arrow and the word, so the direction never depends on color. */
function Change({ value, currency }: { value: ComparedAmount; currency: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const direction = compareMoney(value.difference, '0');
  const Icon = direction > 0 ? ArrowUpRight : direction < 0 ? ArrowDownRight : Minus;
  const words =
    direction > 0
      ? t('reports.compare.more')
      : direction < 0
        ? t('reports.compare.less')
        : t('reports.compare.same');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[13px] tabular-nums',
        direction > 0 ? 'text-expense' : direction < 0 ? 'text-accent' : 'text-muted',
      )}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <span dir="ltr">{f.money(value.difference, currency, true)}</span>
      {value.percent !== null && (
        <span dir="ltr">({t('reports.percent', { percent: value.percent })})</span>
      )}
      <span className="sr-only">{words}</span>
    </span>
  );
}

/** 2. This month vs last month, month to date against the same days of last month. */
function CompareSection({ report }: { report: CompareReport }) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <section className="flex flex-col gap-2" aria-labelledby="compare-title">
      <SectionLabel id="compare-title">{t('reports.compare.title')}</SectionLabel>
      <Card className="flex flex-col gap-2">
        <span className="text-[13px] text-muted">
          {t('reports.compare.periods', {
            current: f.days(report.current.from, report.current.to),
            previous: f.days(report.previous.from, report.previous.to),
          })}
        </span>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col">
            <span className="text-[13px] text-muted">{t('reports.compare.now')}</span>
            <span dir="ltr" className="text-lg font-semibold tabular-nums text-start">
              {f.money(report.overall.current, report.currency)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[13px] text-muted">{t('reports.compare.before')}</span>
            <span dir="ltr" className="text-lg font-semibold tabular-nums text-start">
              {f.money(report.overall.previous, report.currency)}
            </span>
          </div>
        </div>
        <Change value={report.overall} currency={report.currency} />
        {report.monthToDate && (
          <span className="text-xs text-muted">{t('reports.compare.fairHint')}</span>
        )}
      </Card>
      {report.categories.length > 0 && (
        <ListGroup>
          {report.categories.map((c) => (
            <li key={c.categoryId} className="flex min-h-14 items-center gap-3 px-4 py-3">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[15px] font-medium">{c.name}</span>
                <span dir="ltr" className="text-xs text-muted tabular-nums text-start">
                  {t('reports.compare.fromTo', {
                    before: f.money(c.previous, report.currency),
                    now: f.money(c.current, report.currency),
                  })}
                </span>
              </span>
              <Change value={c} currency={report.currency} />
            </li>
          ))}
        </ListGroup>
      )}
    </section>
  );
}

/** 3. Income vs expense: one row per month, two labelled bars and saved or overspent. */
function IncomeExpenseSection({ report }: { report: IncomeExpenseReport }) {
  const { t } = useTranslation();
  const f = useFormat();
  const largest = report.months.reduce(
    (max, m) => [m.income, m.expense].reduce((x, v) => (compareMoney(v, x) > 0 ? v : x), max),
    '0',
  );
  const width = (v: string) => (isNegativeMoney(v) ? '0' : (percentOf(v, largest) ?? '0'));
  const months = [...report.months].reverse();
  const net = (value: string) =>
    isNegativeMoney(value)
      ? t('reports.income.overspent', { amount: f.money(negateMoney(value), report.currency) })
      : t('reports.income.saved', { amount: f.money(value, report.currency) });
  return (
    <section className="flex flex-col gap-2" aria-labelledby="income-title">
      <SectionLabel id="income-title">{t('reports.income.title')}</SectionLabel>
      <Card className="flex flex-col gap-1">
        <span className="text-[13px] text-muted">{t('reports.income.twelveMonths')}</span>
        <span
          className={cn(
            'text-lg font-semibold tabular-nums',
            isNegativeMoney(report.totals.net) ? 'text-expense' : 'text-accent',
          )}
        >
          {net(report.totals.net)}
        </span>
        {!isZeroMoney(report.totals.exchangeDifference) && (
          <span className="text-xs text-muted">
            {t('reports.income.exchangeDifference', {
              amount: f.money(report.totals.exchangeDifference, report.currency, true),
            })}
          </span>
        )}
      </Card>
      <ListGroup>
        {months.map((m) => (
          <li key={m.month} className="flex flex-col gap-2 px-4 py-3">
            <span className="flex items-baseline justify-between gap-3">
              <span className="text-[15px] font-medium">{f.month(m.month)}</span>
              <span
                className={cn(
                  'text-[13px] tabular-nums',
                  isNegativeMoney(m.net) ? 'text-expense' : 'text-muted',
                )}
              >
                {net(m.net)}
              </span>
            </span>
            <span className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-2 text-xs">
              <span className="text-muted">{t('home.income')}</span>
              <Bar percent={width(m.income)} tone="accent" />
              <span dir="ltr" className="tabular-nums text-end">
                {f.money(m.income, report.currency)}
              </span>
              <span className="text-muted">{t('home.expenses')}</span>
              <Bar percent={width(m.expense)} tone="expense" />
              <span dir="ltr" className="tabular-nums text-end">
                {f.money(m.expense, report.currency)}
              </span>
            </span>
            {!isZeroMoney(m.exchangeDifference) && (
              <span className="text-xs text-muted">
                {t('reports.income.exchangeDifference', {
                  amount: f.money(m.exchangeDifference, report.currency, true),
                })}
              </span>
            )}
          </li>
        ))}
      </ListGroup>
    </section>
  );
}

function DebtSide({
  title,
  side,
  currency,
}: {
  title: string;
  side: DebtsReportSide;
  currency: string;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>{title}</CardTitle>
      {side.people.length === 0 ? (
        <p className="text-[13px] text-muted">{t('reports.debts.none')}</p>
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-line">
            {side.people.map((p) => (
              <li key={p.key} className="flex items-start gap-3 py-2">
                <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{p.name}</span>
                <span className="flex flex-col items-end">
                  {p.byCurrency.map((b) => (
                    <span key={b.currency} dir="ltr" className="text-[15px] tabular-nums">
                      {f.money(b.balance, b.currency)}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-1 border-t border-line pt-3">
            <span className="text-[13px] text-muted">{t('reports.debts.total')}</span>
            {side.byCurrency.map((b) => (
              <span
                key={b.currency}
                dir="ltr"
                className="text-[15px] font-semibold tabular-nums text-start"
              >
                {f.money(b.balance, b.currency)}
              </span>
            ))}
            {side.byCurrency.some((b) => b.currency !== currency) && (
              <span className="text-[13px] text-muted">
                {t('reports.debts.atTodaysRate', {
                  amount: `${side.atTodaysRate.estimated ? '≈ ' : ''}${f.money(side.atTodaysRate.amount, currency)}`,
                })}
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

/** 4. Debts: owed to us and by us, per person and currency, what is due soon or overdue. */
function DebtsSection({ report }: { report: DebtsReport }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  return (
    <section className="flex flex-col gap-2" aria-labelledby="debts-title">
      <SectionLabel id="debts-title">{t('reports.debts.title')}</SectionLabel>
      {report.due.length > 0 && (
        <ListGroup>
          {report.due.map((d) => (
            <li key={`${d.debtId}-${d.source}`}>
              <Link
                to={`/debts/${d.debtId}`}
                className="flex min-h-14 items-center gap-3 px-4 py-3 hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[15px] font-medium">{d.personName}</span>
                  <span className="text-xs text-muted">
                    {t(
                      d.source === 'credit' ? 'reports.debts.credit' : 'reports.debts.installment',
                    )}
                    {' · '}
                    {new Intl.DateTimeFormat(i18n.language, {
                      day: 'numeric',
                      month: 'short',
                      timeZone: 'UTC',
                    }).format(new Date(`${d.dueOn}T12:00:00Z`))}
                  </span>
                </span>
                <span className="flex flex-col items-end gap-1">
                  <span dir="ltr" className="text-[15px] tabular-nums">
                    {f.money(d.amount, d.currency)}
                  </span>
                  <span
                    className={cn(
                      'rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold',
                      d.state === 'overdue' ? 'text-expense' : 'text-muted',
                    )}
                  >
                    {t(`reports.debts.state.${d.state}`)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ListGroup>
      )}
      <DebtSide
        title={t('reports.debts.owedToUs')}
        side={report.owedToUs}
        currency={report.currency}
      />
      <DebtSide title={t('reports.debts.weOwe')} side={report.weOwe} currency={report.currency} />
    </section>
  );
}

type Period = 'month' | 'range';

/**
 * Reports (Phase 6): where the money went, this month vs last month, income vs expense and
 * debts. Everything comes from the API, which counts only what the viewer can see; without
 * reports.view it is the viewer's own spending ("My spending").
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const [params, setParams] = useSearchParams();
  const memberId = params.get('member') ?? undefined;
  const requested = params.get('month') ?? undefined;
  const [period, setPeriod] = useState<Period>(params.get('from') ? 'range' : 'month');
  const from = params.get('from') ?? undefined;
  const to = params.get('to') ?? undefined;
  const set = (changes: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );

  const scope = { memberId, month: requested };
  const compare = useQuery({
    queryKey: [...reportsKey, 'compare', scope],
    queryFn: () => api.reports.compare(scope),
  });
  const month = compare.data?.month;
  const range = period === 'range' && from && to && from <= to ? { from, to } : undefined;
  const spending = useQuery({
    queryKey: [...reportsKey, 'spending', memberId, range ?? month],
    queryFn: () => api.reports.spending({ memberId, ...(range ?? { month }) }),
    enabled: month !== undefined,
  });
  const income = useQuery({
    queryKey: [...reportsKey, 'income', scope],
    queryFn: () => api.reports.incomeExpense(scope),
  });
  const debts = useQuery({
    queryKey: [...reportsKey, 'debts', memberId],
    queryFn: () => api.reports.debts({ memberId }),
  });

  // "Export PDF": the server draws the month's reports with only what this viewer can see.
  const exportPdf = useMutation({
    mutationFn: async () => {
      const blob = await api.reports.pdf({ memberId, month });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `familywallet-${month ?? ''}.pdf`;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });

  const familyView = compare.data && compare.data.scope !== 'mine';
  const members = useMembers();
  const people = (members.data?.pages.flatMap((p) => p.items) ?? []).filter(
    (m) => m.status === 'active',
  );
  const current = compare.data?.monthToDate ?? false;
  const error = compare.error ?? spending.error ?? income.error ?? debts.error;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={compare.data?.scope === 'mine' ? t('reports.mineTitle') : t('reports.title')}
        back="/"
        action={
          month && (
            <Button
              variant="small"
              onClick={() => exportPdf.mutate()}
              disabled={exportPdf.isPending}
            >
              <FileDown aria-hidden="true" className="size-4" />
              {exportPdf.isPending ? t('reports.pdf.exporting') : t('reports.pdf.export')}
            </Button>
          )
        }
      />
      {exportPdf.error && <FormError error={exportPdf.error} />}
      {familyView && people.length > 1 && (
        <div
          className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
          role="group"
          aria-label={t('reports.whose')}
        >
          {[{ id: '', displayName: t('reports.wholeFamily') }, ...people].map((m) => {
            const active = (memberId ?? '') === m.id;
            return (
              <button
                key={m.id || 'family'}
                type="button"
                aria-pressed={active}
                onClick={() => set({ member: m.id || null })}
                className={cn(
                  'h-8 shrink-0 rounded-full px-3 text-[13px] font-medium focus-visible:outline-2 focus-visible:outline-accent',
                  active ? 'bg-fg text-bg' : 'bg-surface-2 text-muted',
                )}
              >
                {m.displayName}
              </button>
            );
          })}
        </div>
      )}
      {month && (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label={t('budgets.previousMonth')}
            onClick={() => set({ month: addMonths(month, -1) })}
            className="flex size-11 items-center justify-center rounded-xl text-accent focus-visible:outline-2 focus-visible:outline-accent"
          >
            <ChevronLeft aria-hidden="true" className="size-5 rtl:rotate-180" />
          </button>
          <h2 className="text-[15px] font-semibold" aria-live="polite">
            {f.month(month)}
          </h2>
          <button
            type="button"
            aria-label={t('budgets.nextMonth')}
            disabled={current}
            onClick={() => set({ month: addMonths(month, 1) })}
            className="flex size-11 items-center justify-center rounded-xl text-accent disabled:text-muted disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-accent"
          >
            <ChevronRight aria-hidden="true" className="size-5 rtl:rotate-180" />
          </button>
        </div>
      )}
      {error && <FormError error={error} />}
      {!compare.data && !error && <p role="status">{t('app.loading')}</p>}

      {month && (
        <>
          <Segmented
            label={t('reports.where.period')}
            options={[
              { value: 'month', label: t('reports.where.month') },
              { value: 'range', label: t('reports.where.range') },
            ]}
            value={period}
            onChange={(value) => {
              setPeriod(value);
              if (value === 'month') set({ from: null, to: null });
              else {
                const bounds = monthBounds(month);
                set({ from: from ?? bounds.from, to: to ?? bounds.to });
              }
            }}
          />
          {period === 'range' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-[13px] text-muted">
                {t('reports.where.from')}
                <input
                  type="date"
                  value={from ?? ''}
                  max={to}
                  onChange={(e) => set({ from: e.target.value || null })}
                  className="h-12 rounded-2xl border border-line bg-surface px-3 text-[15px] text-fg"
                />
              </label>
              <label className="flex flex-col gap-1 text-[13px] text-muted">
                {t('reports.where.to')}
                <input
                  type="date"
                  value={to ?? ''}
                  min={from}
                  onChange={(e) => set({ to: e.target.value || null })}
                  className="h-12 rounded-2xl border border-line bg-surface px-3 text-[15px] text-fg"
                />
              </label>
            </div>
          )}
        </>
      )}
      {spending.data && <SpendingSection report={spending.data} />}
      {compare.data && <CompareSection report={compare.data} />}
      {income.data && <IncomeExpenseSection report={income.data} />}
      {debts.data && <DebtsSection report={debts.data} />}
      {compare.data && (
        <p className="px-1 text-xs text-muted">
          {t('reports.countsHint', { currency: compare.data.currency })}
        </p>
      )}
    </div>
  );
}
